import api from './api';
import { CacheTag } from './CacheTag';
import urlConstants from '~/lib/constants/url.constants';

export interface ScriptSureSyncFailure {
  patientId: string;
  firstName: string;
  lastName: string;
  email: string;
  failureReason: string;
  isSynced: boolean;
  updatedAt: string;
}

interface GetScriptSureSyncFailuresResponse {
  data: ScriptSureSyncFailure[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
}

interface GetScriptSureSyncFailuresParams {
  pageNumber: number;
  pageSize: number;
}

interface ReSyncScriptSurePatientResponse {
  isSynced: boolean;
}

interface GetIntercomJWTResponse {
  token: string;
  expiresAt: string;
}

export const integrationsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getIntercomMaudJWT: build.query<GetIntercomJWTResponse, void>({
      query: () => urlConstants.integration.getIntercomMaudJWT,
    }),
    getScriptSurePrescribingURL: build.query<{ url: string }, string>({
      query: (patientExternalId) =>
        urlConstants.integration.getScriptSurePrescribingURL(patientExternalId),
    }),
    getScriptSurePrescriptionMessagesCount: build.query<
      { count: number },
      void
    >({
      query: () =>
        urlConstants.integration.getScriptSurePrescriptionMessagesCount,
    }),
    getScriptSurePrescriptionMessagesURL: build.query<{ url: string }, void>({
      query: () =>
        urlConstants.integration.getScriptSurePrescriptionMessagesURL,
    }),
    getScriptSureSyncFailures: build.query<
      GetScriptSureSyncFailuresResponse,
      GetScriptSureSyncFailuresParams
    >({
      providesTags: [CacheTag.ScriptSureSyncFailures],
      query: ({ pageNumber, pageSize }) => ({
        url: urlConstants.integration.getScriptSureSyncFailures,
        params: {
          'page-number': pageNumber,
          'page-size': pageSize,
        },
      }),
    }),
    reSyncScriptSurePatient: build.mutation<
      ReSyncScriptSurePatientResponse,
      string
    >({
      invalidatesTags: (result) =>
        result?.isSynced ? [CacheTag.ScriptSureSyncFailures] : [],
      query: (patientId) => ({
        url: urlConstants.integration.reSyncScriptSurePatient(patientId),
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useGetIntercomMaudJWTQuery,
  useGetScriptSurePrescribingURLQuery,
  useGetScriptSurePrescriptionMessagesCountQuery,
  useGetScriptSurePrescriptionMessagesURLQuery,
  useGetScriptSureSyncFailuresQuery,
  useReSyncScriptSurePatientMutation,
} = integrationsApi;
